import React from "react";
import {
  config,
  lineConfig,
  areaConfig,
  columnConfig,
  barConfig,
  pieConfig,
  donutConfig,
} from "./SupportFiles/config";
import NoData from "../Info/NoData";
import Line from "./CustomCharts/Line";
import AreaCharts from "./CustomCharts/AreaCharts";
import ColumnCharts from "./CustomCharts/ColumnCharts";
import BarCharts from "./CustomCharts/BarCharts";
import PieCharts from "./CustomCharts/PieCharts";
import DonutCharts from "./CustomCharts/DonutCharts";
import ListChart from "./CustomCharts/ListChart";

const ChartsSetWrapper = ({
  title,
  series = [],
  categories = [],
  type = "line", // line, AreaCharts
  options = {},
  height = "100%",
  width = "100%",
  loading = false,
  chartInfo = { showLabel: true },
}) => {
  const typeName =
    typeof type === "string" ? type : (type && type.name) || "line";
  const typeDefaults =
    typeName === "line"
      ? lineConfig
      : typeName === "area"
      ? areaConfig
      : typeName === "column"
      ? columnConfig
      : typeName === "bar"
      ? barConfig
      : typeName === "pie"
      ? pieConfig
      : typeName === "donut"
      ? donutConfig
      : typeName === "list"
      ? {}
      : {};
  const baseMerged = {
    ...options,
    stroke: { ...(typeDefaults.stroke || {}), ...(options.stroke || {}) },
    fill: { ...(typeDefaults.fill || {}), ...(options.fill || {}) },
    markers: { ...(typeDefaults.markers || {}), ...(options.markers || {}) },
    bar: { ...(typeDefaults.bar || {}), ...(options.bar || {}) },
    line: { ...(typeDefaults.line || {}), ...(options.line || {}) },
    area: { ...(options.area || {}) },
    pie: {
      ...(typeDefaults.pie || {}),
      ...(options.pie || {}),
      infoSection: {
        ...(typeDefaults.pie?.infoSection || {}),
        ...(options.pie?.infoSection || {}),
      },
    },
    list: { ...(options.list || {}) },
    legend: { ...(config.legend || {}), ...(options.legend || {}) },
  };
  const mergedOptions =
    typeof type === "object"
      ? {
          ...baseMerged,
          stroke: {
            ...(typeDefaults.stroke || {}),
            ...(options.stroke || {}),
            ...(type.stroke || {}),
          },
          fill: {
            ...(typeDefaults.fill || {}),
            ...(options.fill || {}),
            ...(type.fill || {}),
          },
          markers: {
            ...(typeDefaults.markers || {}),
            ...(options.markers || {}),
            ...(type.markers || {}),
          },
          bar: {
            ...(typeDefaults.bar || {}),
            ...(options.bar || {}),
            ...(type.bar || {}),
          },
          line: {
            ...(typeDefaults.line || {}),
            ...(options.line || {}),
            ...(type.line || {}),
          },
          area: {
            ...(options.area || {}),
            ...(type.area || {}),
          },
          pie: {
            ...(typeDefaults.pie || {}),
            ...(options.pie || {}),
            ...(type.pie || {}),
            infoSection: {
              ...(typeDefaults.pie?.infoSection || {}),
              ...(options.pie?.infoSection || {}),
              ...(type.pie?.infoSection || {}),
            },
          },
          list: { ...(options.list || {}), ...(type.list || {}) },
          legend: {
            ...(config.legend || {}),
            ...(options.legend || {}),
            ...(type.legend || {}),
          },
        }
      : baseMerged;
  const resolveColorToken = (token) => {
    if (!token) return undefined;
    if (typeof token !== "string") return token;
    if (token.startsWith("#")) return token;
    const parts = token.split("/");
    const base = parts[0];
    const segs = base.split("-");
    const name = segs[0];
    const depth = segs[1];
    let varName = depth ? `--color-${name}-${depth}` : `--color-${name}`;
    if (typeof window !== "undefined") {
      const cssVal = getComputedStyle(document.documentElement)
        .getPropertyValue(varName)
        .trim();
      if (cssVal) return cssVal;
    }
    const tf = {
      "red-50": "#fef2f2",
      "red-100": "#fee2e2",
      "red-200": "#fecaca",
      "red-300": "#fca5a5",
      "red-400": "#f87171",
      "red-500": "#ef4444",
      "red-600": "#dc2626",
      "red-700": "#b91c1c",
      "red-800": "#991b1b",
      "red-900": "#7f1d1d",
      "pink-50": "#fdf2f8",
      "pink-100": "#fce7f3",
      "pink-200": "#fbcfe8",
      "pink-300": "#f9a8d4",
      "pink-400": "#f472b6",
      "pink-500": "#ec4899",
      "pink-600": "#db2777",
      "pink-700": "#be185d",
      "pink-800": "#9d174d",
      "pink-900": "#831843",
      "cyan-50": "#ecfeff",
      "cyan-100": "#cffafe",
      "cyan-200": "#a5f3fc",
      "cyan-300": "#67e8f9",
      "cyan-400": "#22d3ee",
      "cyan-500": "#06b6d4",
      "cyan-600": "#0891b2",
      "cyan-700": "#0e7490",
      "cyan-800": "#155e75",
      "cyan-900": "#164e63",
      "slate-50": "#f8fafc",
      "slate-100": "#f1f5f9",
      "slate-200": "#e2e8f0",
      "slate-300": "#cbd5e1",
      "slate-400": "#94a3b8",
      "slate-500": "#64748b",
      "slate-600": "#475569",
      "slate-700": "#334155",
      "slate-800": "#1e293b",
      "slate-900": "#0f172a",
    };
    const key = depth ? `${name}-${depth}` : name;
    if (tf[key]) return tf[key];
    return token;
  };
  const inputColors = mergedOptions.colors || config.colors;
  const colors = Array.isArray(inputColors)
    ? inputColors.map((c) => resolveColorToken(c))
    : inputColors;
  const resolvedMergedOptions = React.useMemo(() => {
    const bg = resolveColorToken(mergedOptions.background);
    const axis = mergedOptions.axis || {};
    const grid = mergedOptions.grid || {};
    return {
      ...mergedOptions,
      background: bg,
      axis: {
        ...axis,
        color: resolveColorToken(axis.color),
        labelColor: resolveColorToken(axis.labelColor),
      },
      grid: {
        ...grid,
        color: resolveColorToken(grid.color),
        background: resolveColorToken(grid.background),
      },
    };
  }, [mergedOptions]);
  const [hiddenSeriesIdx, setHiddenSeriesIdx] = React.useState([]);
  const [hiddenSliceIdx, setHiddenSliceIdx] = React.useState([]);
  const [hiddenNames, setHiddenNames] = React.useState([]);
  const toggleSeries = (idx) => {
    setHiddenSeriesIdx((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };
  const toggleSlice = (idx) => {
    setHiddenSliceIdx((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };
  const seriesForChart = React.useMemo(() => {
    if (typeName === "pie" || typeName === "donut") {
      if (Array.isArray(series) && typeof series[0] === "number") {
        return series.map((v, i) => (hiddenSliceIdx.includes(i) ? 0 : v));
      }
      if (Array.isArray(series[0]?.data)) {
        const data = series[0].data.map((v, i) =>
          hiddenSliceIdx.includes(i) ? 0 : v
        );
        return [{ ...series[0], data }];
      }
      return series;
    }
    return series
      .filter((s) => !hiddenNames.includes(s?.name))
      .filter((_, idx) => !hiddenSeriesIdx.includes(idx));
  }, [series, typeName, hiddenSeriesIdx, hiddenSliceIdx, hiddenNames]);
  const colorsForChart = React.useMemo(() => {
    if (typeName === "pie" || typeName === "donut") return colors;
    const visibleIdxs = series
      .map((_, idx) => idx)
      .filter((idx) => !hiddenSeriesIdx.includes(idx));
    return visibleIdxs.map((vi) => colors[vi % colors.length]);
  }, [colors, hiddenSeriesIdx, series, typeName]);
  const isGrouped =
    mergedOptions?.bar?.grouped === true ||
    mergedOptions?.chart?.grouped === true;
  const legendDedupeByName = mergedOptions?.legend?.dedupeByName ?? isGrouped;
  const uniqueLegend = React.useMemo(() => {
    if (!legendDedupeByName || !Array.isArray(series)) return [];
    const seen = new Set();
    const items = [];
    series.forEach((s, idx) => {
      const nm = s?.name ?? `Series ${idx + 1}`;
      if (!seen.has(nm)) {
        seen.add(nm);
        items.push({ name: nm, color: colors[idx % colors.length] });
      }
    });
    return items;
  }, [legendDedupeByName, series, colors]);
  const seriesColorMap = React.useMemo(() => {
    if (!legendDedupeByName || !Array.isArray(series)) return {};
    const cmap = {};
    uniqueLegend.forEach((it) => {
      cmap[it.name] = it.color;
    });
    return cmap;
  }, [legendDedupeByName, uniqueLegend]);
  const localOptions = React.useMemo(
    () => ({
      ...resolvedMergedOptions,
      colors: colorsForChart,
      seriesColorMap,
    }),
    [resolvedMergedOptions, colorsForChart, seriesColorMap]
  );
  const localOptionsForType = React.useMemo(() => {
    if (typeName === "list") {
      return {
        ...localOptions,
        legend: { ...(localOptions.legend || {}), show: false },
      };
    }
    return localOptions;
  }, [localOptions, typeName]);
  const renderChart = () => {
    switch (typeName) {
      case "line":
        return (
          <Line
            series={seriesForChart}
            categories={categories}
            options={localOptions}
            height={height}
            width={width}
          />
        );
      case "area":
        return (
          <AreaCharts
            series={seriesForChart}
            categories={categories}
            options={localOptions}
            height={height}
            width={width}
          />
        );
      case "column":
        return (
          <ColumnCharts
            series={seriesForChart}
            categories={categories}
            options={localOptions}
            height={height}
            width={width}
          />
        );
      case "bar":
        return (
          <BarCharts
            series={seriesForChart}
            categories={categories}
            options={localOptions}
            height={height}
            width={width}
          />
        );
      case "pie":
        return (
          <PieCharts
            series={seriesForChart}
            categories={categories}
            options={localOptions}
            height={height}
            width={width}
          />
        );
      case "donut":
        return (
          <DonutCharts
            series={seriesForChart}
            categories={categories}
            options={localOptionsForType}
            height={height}
            width={width}
          />
        );
      case "list":
        return (
          <ListChart
            series={seriesForChart}
            categories={categories}
            options={localOptionsForType}
            height={height}
            width={width}
          />
        );
      default:
        return null;
    }
  };

  const hasData =
    Array.isArray(series) &&
    series.length > 0 &&
    series.some((s) => {
      if (typeof s === "number") return true;
      return Array.isArray(s.data) && s.data.length > 0;
    });

  return (
    <div className="charts-set-wrapper w-[calc(100%-8px)] h-[calc(100%-8px)] m-1 overflow-visible">
      <div className="w-full h-full bg-white flex-1 inputShadow p-1 flex flex-col gap-2 min-h-0 overflow-hidden">
        <div className="flex justify-between items-center gap-2 p-1 border-b border-gray-200">
          <div>{title}</div>
          {/* <div>toolbar End</div> */}
        </div>
        {loading ? (
          <div className="flex-1 flex justify-center items-center">
            <div className="flex items-center gap-2 text-gray-700">
              <span className="inline-block w-3 h-3 rounded-full border-2 border-gray-400 border-t-blue-500 animate-spin" />
              <span className="text-sm">Loading chart...</span>
            </div>
          </div>
        ) : hasData ? (
          <React.Fragment>
            <div className="flex-1 min-h-0 overflow-visible">
              {renderChart()}
            </div>
            {chartInfo.showLabel &&
              mergedOptions?.legend?.mode == "inline" &&
              mergedOptions?.legend?.show !== false &&
              typeName !== "list" && (
                <div className="p-1 border-t border-gray-200 flex justify-center items-center flex-wrap gap-x-5 gap-y-1">
                  {typeName === "pie" || typeName === "donut"
                    ? Array.isArray(categories) &&
                      categories.map((c, idx) => (
                        <div
                          key={`legend-cat-${idx}`}
                          className="flex items-center gap-2 text-sm cursor-pointer select-none"
                          role="button"
                          tabIndex={0}
                          onClick={() => toggleSlice(idx)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ")
                              toggleSlice(idx);
                          }}
                          style={{
                            opacity: hiddenSliceIdx.includes(idx) ? 0.5 : 1,
                          }}
                        >
                          <span
                            className="inline-block"
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: 2,
                              background: colors[idx % colors.length],
                            }}
                          />
                          <span>{c ?? `Slice ${idx + 1}`}</span>
                        </div>
                      ))
                    : legendDedupeByName && uniqueLegend.length > 0
                    ? uniqueLegend.map((it, iidx) => (
                        <div
                          key={`legend-name-${iidx}`}
                          className="flex items-center gap-2 text-sm cursor-pointer select-none"
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            setHiddenNames((prev) =>
                              prev.includes(it.name)
                                ? prev.filter((n) => n !== it.name)
                                : [...prev, it.name]
                            );
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              setHiddenNames((prev) =>
                                prev.includes(it.name)
                                  ? prev.filter((n) => n !== it.name)
                                  : [...prev, it.name]
                              );
                            }
                          }}
                          style={{
                            opacity: hiddenNames.includes(it.name) ? 0.5 : 1,
                          }}
                        >
                          <span
                            className="inline-block"
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: 2,
                              background: it.color,
                            }}
                          />
                          <span>{it.name}</span>
                        </div>
                      ))
                    : Array.isArray(series) &&
                      series.map((s, idx) => (
                        <div
                          key={`legend-${idx}`}
                          className="flex items-center gap-2 text-sm cursor-pointer select-none"
                          role="button"
                          tabIndex={0}
                          onClick={() => toggleSeries(idx)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ")
                              toggleSeries(idx);
                          }}
                          style={{
                            opacity: hiddenSeriesIdx.includes(idx) ? 0.5 : 1,
                          }}
                        >
                          <span
                            className="inline-block"
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: 2,
                              background: colors[idx % colors.length],
                            }}
                          />
                          <span>{s.name ?? `Series ${idx + 1}`}</span>
                        </div>
                      ))}
                </div>
              )}
          </React.Fragment>
        ) : (
          <div className="flex-1 flex justify-center items-center p-10">
            <NoData />
          </div>
        )}
      </div>
    </div>
  );
};

export default ChartsSetWrapper;
